import React, { useState, useEffect, useContext } from 'react'
import Layout from 'components/layout'
import SEO from 'components/seo'
import { graphql } from 'gatsby'
import { injectIntl } from 'gatsby-plugin-intl'
import styled from 'styled-components'
import { colors, breakpoints, richTextOptions as options, transitions } from 'utils/utils'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import 'animate.css/animate.min.css'
import ScrollAnimation from 'react-animate-on-scroll'
import Image from 'gatsby-image'
import HeroContainer from 'components/heroContainer'
import { IoIosArrowDropdownCircle } from 'react-icons/io'
import { GlobalStateContext } from '../context/globalContext'
import { GlobalDispatchContext } from '../context/globalContext'
import { useLocation } from '@reach/router';
import queryString from 'query-string';

export default injectIntl(props => {

    const state = useContext(GlobalStateContext)
    const dispatch = useContext(GlobalDispatchContext)  
     
    useEffect(() => {
        if(state.otherLanguageSlug !== props.pageContext.otherLanguageSlug) {
            dispatch(state.changeOtherLanguageSlug(props.pageContext.otherLanguageSlug))
        }
    }, [state, dispatch, props.pageContext.otherLanguageSlug])

    const { technologies, data, seo, title} = props.data.contentfulSection

    const { fluid } = props.data.hero
    
    const getSelectedTech = (query) => {
        const fallback = 'none'
        if (query) {
          const queriedTech = queryString.parse(query)
          const tech = queriedTech
          // Ensure a valid expected value is passed
          if (tech && tech.tech && tech.tech.includes("tech")) {
            return tech.tech;
          }
        }
        return fallback;
    }
    const location = useLocation()
    const selectedTech = (location.search && getSelectedTech(location.search))

    const [ contentShowed, setContentShowed ] = useState(selectedTech)

    const handleViewMore = (e) => {
        var dataName = e.target.getAttribute("data-name")
        if (e.target.nodeName === 'svg') {
            dataName = e.target.parentElement.getAttribute("data-name")
        }
        if (e.target.nodeName === 'path') {
            dataName = e.target.parentElement.parentElement.getAttribute("data-name")
        }
        if (dataName === contentShowed) {
            dataName = "none"
        }
        setContentShowed(dataName)
    }

    const handleTransitionEnd = (e) => {
        var dataName = e.target.getAttribute("data-name")
        if (dataName === contentShowed) {
            e.target.scrollIntoView(true)
            window.scroll({
                top: window.scrollY - document.querySelector('.headerSection').getBoundingClientRect().height - 100,
                behavior: 'smooth'
            })
        }
    }

    return (
        < >
            <Layout>
                <SEO 
                    title={seo.title}
                    keywords={seo.attributes[0].keywords}
                    slug={seo.slug}
                    description={seo.attributes[0].description}
                    lang={props.pageContext.language}
                />
                <Container>
                    <HeroContainer>
                        <Title>
                            <h1>
                                <ScrollAnimation animateOnce={true} animateIn="fadeInUp">
                                    {title}
                                </ScrollAnimation>
                            </h1>
                        </Title>
                        <HeroImage animateOnce={true} animateIn="fadeIn">
                            <Image fluid={fluid}/>
                        </HeroImage>
                    </HeroContainer>
                    <Summary animateOnce={true} animateIn="fadeInUp">
                            <div>
                                <span>{data.content1}</span>
                                <span>{data.content2}</span>
                                <span>{data.content3}</span>
                            </div>
                    </Summary>
                    <Technologies>
                        {
                            technologies.map((technologie, index) => {
                                return (
                                    <ListBlock key={index} animateOnce={true} animateIn='fadeInUp' delay={200}
                                            className={contentShowed === "tech" + (index + 1) ? "show" : ""}>
                                        <button aria-label={technologie.name} onClick={handleViewMore} data-name={"tech" + (index + 1)}>
                                            <h2>{technologie.title}</h2>
                                            <IoIosArrowDropdownCircle />
                                        </button>
                                        <div onTransitionEnd={handleTransitionEnd} data-name={"tech" + (index + 1)}>
                                        {
                                            technologie.brands.map( data  => {

                                                const { id, name, logo, summary, points } = data
                                                
                                                return (
                                                    <Technologie key={id}>
                                                        <Brand>                            
                                                            <div>
                                                                <ScrollAnimation animateOnce={true} animateIn="fadeInRightBig">
                                                                    <h1>{name}</h1>
                                                                    { documentToReactComponents(summary && summary.json, options) }
                                                                </ScrollAnimation>
                                                            </div>
                                                            <div>
                                                                {typeof logo !== 'undefined' &&
                                                                    <Circle animateOnce={true} animateIn="fadeInRightBig">
                                                                        <img src={logo.file.url} alt={logo.title}/>
                                                                    </Circle>
                                                                }
                                                            </div>
                                                        </Brand>
                                                        <Points>
                                                            <ScrollAnimation animateOnce={true} animateIn="fadeInRightBig">
                                                                { documentToReactComponents(points && points.json, options) }
                                                            </ScrollAnimation>
                                                        </Points>
                                                    </Technologie>
                                                )
                                            })
                                        }
                                        </div>
                                    </ListBlock>
                                )
                            })
                        }
                    </Technologies>
                </Container>
            </Layout>
        </>
    )
})

const Container = styled.div`
    background-color: ${colors.quinary};
`

const Title = styled.div`
    width: 50%;
    background-color: ${colors.primary};
    display: flex;
    justify-content: center;
    align-items: center;
    ${breakpoints.md} {
        width: 100%;
        min-height: 30rem;
    }
    h1 {
        font-size: 4rem;
        color: ${colors.tertiary};
        max-width: 70%;
        margin: auto;
        font-size 3.5rem;
        ${breakpoints.md} {
            margin: auto;
            max-width: 90%;
            text-align: center;
            font-size 2.5rem;
        }
    }
`

const HeroImage = styled(ScrollAnimation)`
    width: 50%;    
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${colors.tertiary};
    ${breakpoints.md} {
        width: 100%;
    }
    div {
        width: 100%;
        height: auto;
        margin: auto;
    }
`

const Summary = styled(ScrollAnimation)`
    text-align: center;
    padding: 6rem 0 3rem;
    width: 70%;
    margin: auto;
    ${breakpoints.md} {
        width: 80%;
    }
`

const Technologies = styled.section`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-evenly;
    padding: 2rem 6rem;
    ${breakpoints.md} {
        flex-direction: column;
        padding: 1rem;
    }
`

const ListBlock = styled(ScrollAnimation)`
    padding: 2rem 2rem 0;
    white-space: pre-wrap;
    padding-bottom: 4rem;
    background-color: ${colors.quaternary};
    width: 48%;
    transition: width .3s ease-in-out;
    min-height: 10rem;
    ${breakpoints.md} {
        width: 100%;
        padding-bottom: 0rem;
    }
    margin: 0rem 0rem 1rem 0rem;
    button {
        border: 0;
        background: transparent;
        cursor: pointer;
        width: 100%;
        position: relative;
        padding: 0px;

        svg {
            height: 2rem;
            width: 2rem;
            transform: translateY(-50%);
            transition: all .3s ease-in-out;
            fill: ${colors.tertiary};
        }
        &:hover {
            h2 {
                transform: scale(1.05);
            }
        }
    }
    h2 {
        font-size: 2.1rem;
        text-align: center;
        pointer-events: none;
        margin: .83rem 0 2rem 0;
        color: ${colors.tertiary};
        transition: ${transitions.button};
    }
    & > div {
        max-height: 0px;
        overflow: hidden;
        transition: max-height .3s ease-in-out;
    }
    &.show {
        width: 100%;
        > div {
            max-height: 1000rem;
            > div {
                margin: 2rem 2rem;
                ${breakpoints.md} {
                    margin: 1rem 0rem;
                }
            }
        }
        svg {
            transform: translateY(-50%) rotate(180deg);
            position: absolute;
            top: 50%;
            left: 80%;
            ${breakpoints.md} {
                position: unset;
            }
        }
    }
`

const Technologie = styled.div`
   display: flex;
   ${breakpoints.md} {
       flex-direction: column;
       margin: 1rem;
   }
   &:not(:first-child) {
       margin-top: 0;
   }
`

const Brand = styled.div`
    overflow: hidden;
    position: relative;
    width: 50%;
    display: flex;
    background-color: ${colors.tertiary};
    margin-right: 1rem;
    padding: 2rem;
    ${breakpoints.md} {
        width: 100%;
    }
    > div:first-child {
        padding: 1rem;
        max-width: 70%;
        margin-left: 0;
    }
    > div:not(:first-child) {
        position: absolute;
        top: 50%;
        right: 5%;
        transform: translateY(-50%);
    }
    
`
    
const Circle = styled(ScrollAnimation)`
    height: 7rem;
    width: 7rem;
    border-radius: 50%;
    background-color: ${colors.tertiary};
    box-shadow: 0 0 .5rem 0 #D6D6D6;
    overflow: hidden;
    img {
        display: block;
        width: auto;
        height: 100%;
        margin: auto;
        ${breakpoints.md} {
            max-width: 150px;
        }
    }
    
`

const Points = styled.div`
    overflow: hidden;
    width: 50%;
    background-color: ${colors.tertiary};
    display: flex;
    justify-content: center;
    align-items: center;
    ${breakpoints.md} {
        width: 100%;
        justify-content: start;
        padding-bottom: 4rem;
    }

    .animated.fadeInRightBig {
        max-width: 530px;
        width: 100%;
      > ul {
        list-style: disc;       
            p {
                margin: .5rem;
            }
        }
    }
`

export const query = graphql`
    query($language: String) {
        hero : contentfulAsset(contentful_id: {eq: "3vPAx6qeU2XChiZeaIOWDW"}) {
            id
            title
            fluid(quality: 90, maxWidth: 1920) {
                ...GatsbyContentfulFluid_withWebp_noBase64
      
            }
        }
        contentfulSection(name: {eq: "Tecnologías"}, node_locale: {eq: $language}) {
            id
            title
              seo {
                title
                slug
                attributes {
                  description
                  keywords
                }
              }
              data {
                content1
                content2
                content3
              }
              technologies {
				name
				title
                brands {
                  id
                  name
                  logo {
                    title
                    file {
                      url
                    }
                  }
                  summary : childContentfulBrandSummaryRichTextNode {
                    json
                  }
                  points : childContentfulBrandPointsRichTextNode {
                    json
                  }
                }
              }
        }
    }

`
